// Pure helpers for the "Shrink PDF" tool (pdf_shrink_controller + the
// on-demand pdf_shrink.js bundle). Kept free of DOM/pdf.js so they can be
// unit-tested in node (test/javascript/pdf_shrink_helpers_test.mjs).

// Compression levels offered in the UI. `quality` is the JPEG quality used for
// each rasterized page; `scale` is the render resolution relative to the
// page's native PDF size (1 = 72 dpi, 1.5 = 108 dpi, 2 = 144 dpi).
export const SHRINK_LEVELS = Object.freeze({
  standard: Object.freeze({ quality: 0.7, scale: 1.5 }),
  smaller: Object.freeze({ quality: 0.5, scale: 1.5 }),
  smallest: Object.freeze({ quality: 0.3, scale: 1.25 })
})

export const DEFAULT_SHRINK_LEVEL = "standard"

export function shrinkSettings(level) {
  return SHRINK_LEVELS[level] || SHRINK_LEVELS[DEFAULT_SHRINK_LEVEL]
}

export function formatBytes(bytes) {
  const value = Number(bytes)
  if (!Number.isFinite(value) || value <= 0) return "0 B"
  const units = ["B", "KB", "MB", "GB"]
  const exponent = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1)
  const scaled = value / Math.pow(1024, exponent)
  const digits = exponent === 0 || scaled >= 100 ? 0 : 1
  return `${Number(scaled.toFixed(digits))} ${units[exponent]}`
}

// Summarize an original → shrunk size change for display.
// Returns { smaller, percent, label }.
export function reductionSummary(originalBytes, newBytes) {
  const original = Number(originalBytes)
  const shrunk = Number(newBytes)
  if (!Number.isFinite(original) || original <= 0 || !Number.isFinite(shrunk)) {
    return { smaller: false, percent: 0, label: "Size unchanged" }
  }
  // Floor (and cap at 99) so a 99.7% cut never reads as "100% smaller".
  const percent = Math.min(Math.floor(((original - shrunk) / original) * 100), 99)
  // A sub-1% saving isn't worth calling a win.
  if (percent < 1) {
    return { smaller: false, percent: 0, label: "Not smaller — the original is already compact" }
  }
  return { smaller: true, percent, label: `${percent}% smaller` }
}

// "application.pdf" -> "application (shrunk).pdf"
export function shrunkFilename(name) {
  const base = String(name || "document.pdf").trim() || "document.pdf"
  const stem = base.replace(/\.pdf$/i, "")
  return `${stem} (shrunk).pdf`
}

export function isPdfFile(file) {
  if (!file) return false
  if (file.type === "application/pdf") return true
  return /\.pdf$/i.test(file.name || "")
}
